import * as React from 'react';

import {
  Button,
  Col,
  ConsoleDialog,
  Row,
  Select,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { LucideShieldCheck } from 'lucide-react';

import * as complianceRulesState from '../../_state/complianceRules.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

type RuleDefinitionInfo = {
  dataType: string;
  allowedValues?: unknown;
};

type ConfiguredValueInputProps = {
  selectedDef: RuleDefinitionInfo;
  configuredValue: string;
  onConfiguredValueChange: (value: string) => void;
  booleanValue: boolean;
  onBooleanValueChange: (value: boolean) => void;
  allowedValueOptions: Array<{ key: string; label: string }>;
  translations: ReturnType<
    typeof useTranslations
  >['complianceView']['rules']['dialog'];
};

const ConfiguredValueInput = ({
  selectedDef,
  configuredValue,
  onConfiguredValueChange,
  booleanValue,
  onBooleanValueChange,
  allowedValueOptions,
  translations: dt,
}: ConfiguredValueInputProps) => {
  if (selectedDef.dataType === 'Boolean') {
    return (
      <Row className="items-center gap-3">
        <Switch isSelected={booleanValue} onChange={onBooleanValueChange} />
        <span className="text-sm">{dt.fields.configuredValue}</span>
      </Row>
    );
  }

  if (selectedDef.dataType === 'Select' && allowedValueOptions.length > 0) {
    return (
      <Select
        label={dt.fields.configuredValue}
        value={configuredValue}
        onChange={onConfiguredValueChange}
        options={allowedValueOptions}
        placeholder={dt.placeholders.defaultValue}
      />
    );
  }

  const isNumeric =
    selectedDef.dataType === 'Integer' ||
    selectedDef.dataType === 'Decimal' ||
    selectedDef.dataType === 'Money' ||
    selectedDef.dataType === 'Weight';

  return (
    <TextField
      label={dt.fields.configuredValue}
      value={configuredValue}
      onChange={onConfiguredValueChange}
      inputMode={isNumeric ? 'numeric' : undefined}
      placeholder={
        isNumeric
          ? selectedDef.dataType === 'Integer'
            ? dt.placeholders.integerValue
            : dt.placeholders.decimalValue
          : dt.placeholders.defaultValue
      }
      className="@ConfiguredValueInput"
    />
  );
};

const parseConfiguredValueString = (
  configuredValue: unknown,
): string | undefined => {
  if (configuredValue == null) return undefined;
  if (
    typeof configuredValue === 'object' &&
    configuredValue !== null &&
    'value' in (configuredValue as Record<string, unknown>)
  ) {
    return String((configuredValue as Record<string, unknown>).value);
  }
  return JSON.stringify(configuredValue);
};

export const FacilityRuleFormDialog = () => {
  const t = useTranslations();
  const [selectedRuleDefId, setSelectedRuleDefId] = React.useState('');
  const [isEnabled, setIsEnabled] = React.useState(true);
  const [configuredValue, setConfiguredValue] = React.useState('');
  const [booleanValue, setBooleanValue] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const editingRuleId = useAtomValue(complianceRulesState.$editingFacilityRule);
  const facilityId = useAtomValue(complianceRulesState.$complianceFacilityId);
  const ruleDefinitions = useAtomValue(
    complianceRulesState.$rawRuleDefinitions,
  );
  const currentRules = useAtomValue(complianceRulesState.$currentFacilityRules);
  const [isOpen, setIsOpen] = useAtom(
    complianceRulesState.$isFacilityRuleDialogOpen,
  );
  const [{ isPending: isCreating }, createRule] = useAtom(
    complianceRulesState.$createFacilityRule,
  );
  const [{ isPending: isUpdating }, updateRule] = useAtom(
    complianceRulesState.$updateFacilityRule,
  );
  const toast = useToast();

  const editingRule = React.useMemo(
    () => currentRules.data?.find((r) => r.id === editingRuleId),
    [currentRules.data, editingRuleId],
  );
  const isEditing = !!editingRule;
  const isPending = isCreating || isUpdating;

  React.useEffect(() => {
    if (isOpen && editingRule) {
      setSelectedRuleDefId(editingRule.ruleDefinitionId);
      setIsEnabled(editingRule.isEnabled);
      const valueStr = parseConfiguredValueString(editingRule.configuredValue);
      setConfiguredValue(valueStr ?? '');
      setBooleanValue(valueStr?.toLowerCase() === 'true');
      setNotes(editingRule.notes ?? '');
    } else if (isOpen) {
      setSelectedRuleDefId('');
      setIsEnabled(true);
      setConfiguredValue('');
      setBooleanValue(false);
      setNotes('');
    }
  }, [isOpen, editingRule]);

  const ruleDefOptions = React.useMemo(() => {
    const existingDefIds = new Set(
      (currentRules.data ?? []).map((r) => r.ruleDefinitionId),
    );
    return (ruleDefinitions.data ?? [])
      .filter((def) => def.isActive)
      .filter((def) => isEditing || !existingDefIds.has(def.id))
      .map((def) => ({
        key: def.id,
        label: `${def.name} (${def.ruleCode})`,
        searchable: `${def.name} ${def.ruleCode} ${def.category}`,
      }));
  }, [ruleDefinitions.data, currentRules.data, isEditing]);

  const selectedDef = React.useMemo(
    () => ruleDefinitions.data?.find((d) => d.id === selectedRuleDefId),
    [ruleDefinitions.data, selectedRuleDefId],
  );

  const allowedValueOptions = React.useMemo(() => {
    const av = selectedDef?.allowedValues;
    if (!av || typeof av !== 'object' || Array.isArray(av)) return [];
    return Object.entries(av as Record<string, unknown>).map(([key, val]) => ({
      key,
      label: String(val ?? key),
    }));
  }, [selectedDef]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const buildConfiguredValue = () => {
    if (selectedDef?.dataType === 'Boolean') {
      return { value: booleanValue };
    }
    if (!configuredValue) return undefined;
    if (selectedDef?.dataType === 'Integer') {
      const parsed = parseInt(configuredValue, 10);
      if (Number.isNaN(parsed)) return undefined;
      return { value: parsed };
    }
    if (
      selectedDef?.dataType === 'Decimal' ||
      selectedDef?.dataType === 'Money' ||
      selectedDef?.dataType === 'Weight'
    ) {
      const parsed = parseFloat(configuredValue);
      if (Number.isNaN(parsed)) return undefined;
      return { value: parsed };
    }
    return { value: configuredValue };
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && editingRule) {
        await updateRule({
          id: editingRule.id,
          isEnabled,
          configuredValue: buildConfiguredValue(),
          notes: notes || undefined,
        });
        toast.success(t.complianceView.rules.dialog.messages.updateSuccess);
      } else {
        if (!facilityId || !selectedRuleDefId) return;
        await createRule({
          facilityId,
          ruleDefinitionId: selectedRuleDefId,
          isEnabled,
          configuredValue: buildConfiguredValue(),
          notes: notes || undefined,
        });
        toast.success(t.complianceView.rules.dialog.messages.createSuccess);
      }
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isEditing) {
        toast.error(t.complianceView.rules.dialog.messages.updateError(msg));
      } else {
        toast.error(t.complianceView.rules.dialog.messages.createError(msg));
      }
    }
  };

  const canSubmit = isEditing || (selectedRuleDefId && facilityId);

  return (
    <ConsoleDialog
      className="@FacilityRuleFormDialog"
      title={
        isEditing
          ? t.complianceView.rules.dialog.editTitle
          : t.complianceView.rules.dialog.createTitle
      }
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={selectedRuleDefId !== '' || configuredValue !== ''}
      content={
        <Col gap6>
          <FormSection
            icon={LucideShieldCheck}
            title={t.complianceView.rules.dialog.sections.rule}
          >
            <Col gap4>
              {!isEditing && (
                <Select
                  label={t.complianceView.rules.dialog.fields.ruleDefinition}
                  value={selectedRuleDefId}
                  onChange={setSelectedRuleDefId}
                  options={ruleDefOptions}
                  placeholder={
                    t.complianceView.rules.dialog.placeholders.ruleDefinition
                  }
                  isRequired
                />
              )}
              {selectedDef && (
                <div className="rounded bg-neutral/5 p-3 text-sm text-neutral/70">
                  <div className="font-medium">{selectedDef.category}</div>
                  {selectedDef.description && (
                    <div className="mt-1">{selectedDef.description}</div>
                  )}
                  <div className="mt-1">
                    {t.complianceView.rules.dialog.ruleInfo.type}:{' '}
                    {selectedDef.dataType} |{' '}
                    {t.complianceView.rules.dialog.ruleInfo.enforcement}:{' '}
                    {selectedDef.enforcementLevel}
                  </div>
                </div>
              )}
              {selectedDef && (
                <ConfiguredValueInput
                  selectedDef={selectedDef}
                  configuredValue={configuredValue}
                  onConfiguredValueChange={setConfiguredValue}
                  booleanValue={booleanValue}
                  onBooleanValueChange={setBooleanValue}
                  allowedValueOptions={allowedValueOptions}
                  translations={t.complianceView.rules.dialog}
                />
              )}
              <Row className="items-center gap-3">
                <Switch isSelected={isEnabled} onChange={setIsEnabled} />
                <span className="text-sm">
                  {t.complianceView.rules.dialog.fields.isEnabled}
                </span>
              </Row>
              <TextField
                label={t.complianceView.rules.dialog.fields.notes}
                value={notes}
                onChange={setNotes}
                placeholder={t.complianceView.rules.dialog.placeholders.notes}
              />
            </Col>
          </FormSection>
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleSubmit}
        >
          {isEditing
            ? t.complianceView.rules.dialog.actions.save
            : t.complianceView.rules.dialog.actions.create}
        </Button>
      }
    />
  );
};
